/// <amd-module name="Wit/BoardsHub/Common/KanbanBoardContracts" />
import { ExceptionInfo } from "Agile/Contracts/Common";
import { WebApiTeam } from "Tfs/Platform/Contracts/Core";
import { IObservableArray, IObservableValue } from "VSS/Core/Observable";
import { IIdentity } from "VSSUI/IdentityPicker";
import { BoardModel } from "Wit/BoardsHub/Common/Client/Contracts/Models";
import { BoardColumn, BoardRow, CardRule } from "Wit/BoardsHub/Common/Client/Contracts/Settings";
import { IObservableBoardsWorkItem } from "Wit/BoardsHub/Common/Interfaces";
import { FieldLayout, FieldLayoutMap } from "Wit/Common/Controls/Boards/Interfaces";
import { FieldType } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { FilterManager, FilterState } from "Wit/Filter/FilterManager";
import { WorkItemTypeColorAndIcon } from "Wit/UI/WorkItemTypeIcon";

// Data provider contracts
// NOTE: ideally we would be generating these, but BacklogContext references server contracts that can't, or at least shouldn't, be generated

export interface BoardData {
    backlogContextData: BacklogContext;
    boardModel: BoardModel;
    isTestPlansEnabled: boolean;
    hasTestPlansLicense: boolean;
    signalrHubUrl: string;
    initialFilters: FilterState;
    enabledWorkItemTypes: string[];
    exceptionInfo: ExceptionInfo;
}

export interface BoardFieldNames {
    columnField: string;
    doneField: string;
    rowField: string;
}

export interface BacklogContext {
    actionNameFromMru: string;
    includeParents: boolean;
    levelName: string;
    portfolios: string[];
    showInProgress: boolean;
    team: WebApiTeam;
    updateUrlActionAndParameter: boolean;
}

export interface BoardFieldDefinition {
    // unfortunately the capitalized names are correct
    Id: number;
    IsEditable: boolean;
    IsIdentity: boolean;
    Name: string;
    ReferenceName: string;
    Type: FieldType;
}

export interface WorkItemPayload {
    columns: string[];
    orderedIncomingIds: number[];
    orderedOutgoingIds: number[];
    rows: any[][];
}

export interface WorkItemSource {
    fieldDefinitions: BoardFieldDefinition[];
    itemTypes: string[];
    payload: WorkItemPayload;
    transitions: { [key: string]: { [key: string]: string[] } };
    type: string;
}

// Client-side contracts

export type WorkItemTypeColorAndIconMap = { [type: string]: WorkItemTypeColorAndIcon | undefined };

export interface BoardLayout {
    /**
     * List of work item types and their display metadata that exist in the project
     */
    backlogLevelWorkItemTypeColorAndIcons: WorkItemTypeColorAndIcon[];
    /**
     * Backlog level id
     */
    backlogLevelId: string;
    /**
     * List of work item types and their display metadata that exist in the project
     */
    workItemTypeColorAndIcons: WorkItemTypeColorAndIcon[];
    /**
     * Map of work item type to color and icon.
     */
    workItemTypeColorAndIconsMap: WorkItemTypeColorAndIconMap;
    /**
     * Full list of columns - used to construct column headings
     */
    columns: BoardColumnLayout[];
    /**
     * Full list of rows - there is always at least one row, and each row contains a partial column for each in-progress column
     */
    rows: BoardRowLayout[];
    /**
     * Incoming column - there will always be one incoming column
     */
    incomingColumn: BoardColumnLayout;
    /**
     * outgoing column - there will always be one outgoing column
     */
    outgoingColumn: BoardColumnLayout;
    /**
     * Defines the fields that should be displayed on a card for the given work item type.
     */
    cardLayout: { [type: string]: CardLayout };
    /**
     * Mapping of tag names to colors
     */
    tagStyles?: Map<string, string>;
    /**
     * List of work items that should be displayed, determined by the current filter state
     */
    filteredIds?: Set<number>;
    /**
     * List of work items that should be displayed, determined by the current enabled value
     */
    enabledWorkItemTypes: Set<string>;
}

export interface IAnnotationSetting extends Pick<CardRule, "isEnabled"> {
    id: string;
    name: string;
}

export interface IBoardSettingsData {
    annotationSettings: IAnnotationSetting[];
    boardData: BoardData;
    boardFields: BoardFieldNames;
    effortField?: IFieldEntry;
    orderField?: IFieldEntry;
    personIdentities?: IIdentity[];
}

export interface CardLayout {
    coreFields: FieldLayoutMap;
    additionalFields: FieldLayout[];
    showEmptyFields: boolean;
}

export interface BoardColumnLayout extends BoardColumn {
    /**
     * Column filter manager, supports text filtering for Incoming column
     */
    columnFilterManager?: FilterManager;
    /**
     * Indicates that this is a column representing the right half of a split column
     */
    isDone?: boolean;
    expanded?: IObservableValue<boolean>;
    /**
     * Indicates this column has work items that can be paged. Useful when we don't receive all of a column's work items in the initial payload.
     */
    hasPartialItems?: IObservableValue<boolean>;
    rowId?: string;
    workItems: IObservableArray<IObservableBoardsWorkItem>;
}

export interface BoardRowLayout extends BoardRow {
    /**
     * Partial columns contained within the row
     */
    columns: BoardColumnLayout[];
    expanded: IObservableValue<boolean>;
}
